/**
 * AnnotationList
 * Sidebar-Panel: listet alle Annotationen der aktuellen Punktwolke.
 */

import React, { useState, useMemo } from 'react'
import { useAnnotationStore } from '../../store/annotations'
import { useSceneStore } from '../../store/scenes'
import {
  AnnotationType,
  ANNOTATION_TYPE_LABELS,
  ANNOTATION_TYPE_COLORS,
} from '../../types/annotations'
import { useCameraNavigation } from '../../hooks/useCameraNavigation'

interface AnnotationListProps {
  currentCloudId: string | null
  projectCloudIds?: string[]
  viewerRef: React.RefObject<any>
  onClose?: () => void
}

export function AnnotationList({ currentCloudId, projectCloudIds = [], viewerRef, onClose }: AnnotationListProps) {
  const annotations = useAnnotationStore(s => s.annotations)
  const startPlacing = useAnnotationStore(s => s.startPlacingAnnotation)
  const openView = useAnnotationStore(s => s.openAnnotationView)
  const openEdit = useAnnotationStore(s => s.openAnnotationEditor)
  const deleteAnnotation = useAnnotationStore(s => s.deleteAnnotation)
  const duplicateAnnotation = useAnnotationStore(s => s.duplicateAnnotation)
  const startRelocating = useAnnotationStore(s => s.startRelocatingAnnotation)
  const setSelectedAnnotation = useAnnotationStore(s => s.setSelectedAnnotation)
  const { flyToCamera } = useCameraNavigation({ viewerRef })

  // Scene-Zuordnung
  const activeSceneId = useSceneStore(s => s.activeSceneId)
  const scenes = useSceneStore(s => s.scenes)
  const toggleAnnotationInScene = useSceneStore(s => s.toggleAnnotationInScene)
  const activeScene = useMemo(() =>
    activeSceneId ? scenes.find(sc => sc.id === activeSceneId) : null,
    [scenes, activeSceneId]
  )

  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)

  const search = useAnnotationStore(s => s.searchQuery)
  const setSearch = useAnnotationStore(s => s.setSearchQuery)
  const filterType = useAnnotationStore(s => s.filterType)
  const setFilterType = useAnnotationStore(s => s.setFilterType)

  const cloudAnnotations = useMemo(
    () => annotations.filter(a => {
      if (!currentCloudId) return true
      if (projectCloudIds.length > 0) return projectCloudIds.includes(a.cloudId)
      return a.cloudId === currentCloudId
    }),
    [annotations, currentCloudId, projectCloudIds.join(',')]
  )

  const filtered = useMemo(() => {
    let result = cloudAnnotations
    if (filterType !== 'all') result = result.filter(a => a.type === filterType)
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        a =>
          a.title.toLowerCase().includes(q) ||
          a.tooltip.toLowerCase().includes(q) ||
          a.tags.some(t => t.toLowerCase().includes(q))
      )
    }
    return result
  }, [cloudAnnotations, filterType, search])

  const usedTypes = useMemo(() => {
    const types = new Set(cloudAnnotations.map(a => a.type))
    return Array.from(types) as AnnotationType[]
  }, [cloudAnnotations])

  return (
    <div
      data-component="AnnotationList"
      className="ghost-panel"
    >
      {/* Search + Filters */}
      <div data-section="annotation-search-filters" className="ghost-search">
        <input
          data-element="input-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Suchen …"
          className="ghost-search__input"
        />
        <div data-section="filter-chips" className="ghost-filters__row">
          <FilterChip
            label="Alle"
            active={filterType === 'all'}
            onClick={() => setFilterType('all')}
          />
          {usedTypes.map(t => (
            <FilterChip
              key={t}
              label={ANNOTATION_TYPE_LABELS[t]}
              active={filterType === t}
              color={ANNOTATION_TYPE_COLORS[t]}
              onClick={() => setFilterType(filterType === t ? 'all' : t)}
            />
          ))}
        </div>
      </div>

      {/* List */}
      <div data-section="annotation-items" className="ghost-list">
        {filtered.length === 0 && (
          <div className="ghost-empty">
            {cloudAnnotations.length === 0
              ? 'Noch keine Notizen. Klicke auf "+" um zu starten.'
              : 'Keine Ergebnisse für diese Suche.'}
          </div>
        )}
        {filtered.map(annotation => {
          const color = ANNOTATION_TYPE_COLORS[annotation.type]
          const isHovered = hoveredItemId === annotation.id
          return (
            <div
              key={annotation.id}
              data-component="AnnotationListItem"
              data-annotation-id={annotation.id}
              data-annotation-type={annotation.type}
              onClick={() => {
                ;(window as any).resetClipBoxToBounds?.()
                flyToCamera(annotation.camera)
                setSelectedAnnotation(annotation.id)
              }}
              className="ghost-item"
              onMouseEnter={() => setHoveredItemId(annotation.id)}
              onMouseLeave={() => setHoveredItemId(null)}
            >
              {/* Scene toggle (nur sichtbar wenn Scene aktiv) */}
              {activeScene && (
                <button
                  title={activeScene.annotationIds.includes(annotation.id)
                    ? `Aus Scene "${activeScene.title}" entfernen`
                    : `Zu Scene "${activeScene.title}" hinzufügen`}
                  onClick={e => {
                    e.stopPropagation()
                    toggleAnnotationInScene(activeScene.id, annotation.id)
                  }}
                  style={{
                    width: '16px', height: '16px', flexShrink: 0,
                    border: `1.5px solid ${activeScene.annotationIds.includes(annotation.id) ? '#1a1a1a' : '#d1d5db'}`,
                    borderRadius: '3px', cursor: 'pointer', padding: 0,
                    background: activeScene.annotationIds.includes(annotation.id) ? '#1a1a1a' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: '1px',
                    transition: `all 0.15s ease`,
                  }}
                >
                  {activeScene.annotationIds.includes(annotation.id) && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              )}

              <div className="ghost-item__content">
                <div className="ghost-item__title">
                  {annotation.title}
                </div>
              </div>

              {/* Type label ODER Hover-Actions */}
              {isHovered ? (
                <div
                  data-section="list-item-actions"
                  className="ghost-actions"
                  onClick={e => e.stopPropagation()}
                >
                  <ListItemAction
                    title="Bearbeiten"
                    onClick={() => { flyToCamera(annotation.camera); openEdit(annotation.id) }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                    </svg>
                  </ListItemAction>
                  <ListItemAction
                    title="Verschieben"
                    onClick={() => startRelocating(annotation.id)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/>
                      <polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/>
                      <line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/>
                    </svg>
                  </ListItemAction>
                  <ListItemAction
                    title="Duplizieren"
                    onClick={() => {
                      const dup = duplicateAnnotation(annotation.id)
                      if (dup) openEdit(dup.id)
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </ListItemAction>
                  <ListItemAction
                    title="Löschen"
                    danger
                    onClick={() => deleteAnnotation(annotation.id)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </ListItemAction>
                </div>
              ) : (
                <span style={{
                  fontSize: '0.5625em',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color,
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                  marginTop: '0.3333em',
                }}>
                  {ANNOTATION_TYPE_LABELS[annotation.type]}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── FilterChip ────────────────────────────────────────────────────────────────

function FilterChip({
  label,
  active,
  color,
  onClick,
}: {
  label: string
  active: boolean
  color?: string
  onClick: () => void
}) {
  return (
    <button
      data-component="FilterChip"
      data-filter={label}
      data-active={active}
      onClick={onClick}
      className={`ghost-chip ${active ? 'ghost-chip--active' : ''}`}
      style={{ color: active ? color : undefined }}
    >
      {label}
    </button>
  )
}

// ─── ListItemAction ─────────────────────────────────────────────────────────

function ListItemAction({
  title,
  danger,
  onClick,
  children,
}: {
  title: string
  danger?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      data-element={`list-action-${title.toLowerCase()}`}
      title={title}
      onClick={e => { e.stopPropagation(); onClick() }}
      className={`ghost-action-btn ${danger ? 'ghost-action-btn--danger' : ''}`}
    >
      {children}
    </button>
  )
}
